import React, { useState, FormEvent } from 'react';
import { useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Clipboard } from 'lucide-react';
import { toast } from '@/components/ui/toast';

// Define the GraphQL mutation
const ISSUE_TOKEN = gql`
  mutation IssueToken($username: String!) {
    issueToken(username: $username)
  }
`;

export const TokenIssuerPage = () => {
  const [username, setUsername] = useState<string>('');
  const [token, setToken] = useState<string | null>(null);
  
  // Set up the mutation
  const [issueToken, { loading }] = useMutation(ISSUE_TOKEN, {
    onCompleted: (data) => {
      const tokenValue = data.issueToken;
      setToken(tokenValue);
      toast.success('Token issued successfully!');
    },
    onError: (error) => {
      toast.error(`Error: ${error.message}`);
    }
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!username.trim()) {
      toast.warn('Please enter a username');
      return;
    }
    
    issueToken({ variables: { username: username.trim() } });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => toast.info('Copied to clipboard!'))
      .catch(() => toast.error('Failed to copy'));
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-100">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Issue Token</CardTitle>
          <CardDescription>Generate a token for a username</CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                disabled={loading}
              />
            </div>
            
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Issuing...' : 'Issue Token'}
            </Button>
          </form>

          {token && (
            <div className="mt-6 p-4 border rounded-md bg-gray-50">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-medium">Token:</h3>
              </div>
              
              <div className="relative">
                <div className="p-3 bg-gray-100 rounded border text-sm font-mono overflow-x-auto break-all">
                  {token}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => copyToClipboard(token)}
                >
                  <Clipboard className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
        
        <CardFooter className="flex justify-center">
          <p className="text-xs text-gray-500">
            Tokens are valid for the duration specified in your system configuration
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

